import { style, styleVariants } from '@vanilla-extract/css'
import { recipe } from '@vanilla-extract/recipes'
import { vars } from '../theme.css'

const gapVariants = {
  0: { gap: vars.space[0] },
  1: { gap: vars.space[1] },
  2: { gap: vars.space[2] },
  3: { gap: vars.space[3] },
  4: { gap: vars.space[4] },
  5: { gap: vars.space[5] },
  6: { gap: vars.space[6] },
  7: { gap: vars.space[7] },
  8: { gap: vars.space[8] },
  9: { gap: vars.space[9] },
} as const

// Stack — vertical flow with a token gap. The workhorse for editorial columns.
export const stack = recipe({
  base: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  variants: {
    gap: gapVariants,
    align: {
      start: { alignItems: 'flex-start' },
      center: { alignItems: 'center' },
      end: { alignItems: 'flex-end' },
      stretch: { alignItems: 'stretch' },
    },
  },
  defaultVariants: { gap: 4 },
})

// Cluster — horizontal group that wraps, with a token gap.
export const cluster = recipe({
  base: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', minWidth: 0 },
  variants: {
    gap: gapVariants,
    align: {
      start: { alignItems: 'flex-start' },
      center: { alignItems: 'center' },
      baseline: { alignItems: 'baseline' },
      end: { alignItems: 'flex-end' },
    },
    justify: {
      start: { justifyContent: 'flex-start' },
      between: { justifyContent: 'space-between' },
      center: { justifyContent: 'center' },
      end: { justifyContent: 'flex-end' },
    },
    nowrap: { true: { flexWrap: 'nowrap' } },
  },
  defaultVariants: { gap: 3 },
})

// Grid — auto-fill responsive grid; column min-width chosen by variant.
export const grid = recipe({
  base: { display: 'grid', minWidth: 0 },
  variants: {
    gap: gapVariants,
    min: {
      xs: { gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))' },
      sm: { gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' },
      md: { gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' },
      lg: { gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))' },
    },
  },
  defaultVariants: { gap: 4, min: 'sm' },
})

// A ruled label/value list — the account drawer's settings and the admin console's read-outs
// were two byte-identical copies of this. It is the same furniture as the Journal's record
// strip: a label on the left, its value on the right, a soft rule between rows.
export const ruledList = style({ listStyle: 'none', margin: 0, padding: 0 })
export const ruledRow = style({
  display: 'flex',
  alignItems: 'center',
  gap: vars.space[3],
  padding: `${vars.space[3]} 0`,
  borderBottom: `1px solid ${vars.color.rule}`,
})
export const ruledLabel = style({
  flex: 1,
  minWidth: 0,
  fontFamily: vars.font.serif,
  fontSize: vars.fs.md,
  color: vars.color.ink,
})
export const ruledValue = style({
  fontFamily: vars.font.mono,
  fontSize: vars.ms.md,
  fontVariantNumeric: 'tabular-nums',
  color: vars.color.ink2,
  textAlign: 'right',
})

export const rule = styleVariants({
  h: { border: 'none', borderTop: `1px solid ${vars.color.rule}`, margin: 0, width: '100%' },
  v: { border: 'none', borderLeft: `1px solid ${vars.color.rule}`, margin: 0, alignSelf: 'stretch' },
})

// Panel — a ruled editorial section (never a rounded card).
export const panel = recipe({
  base: { background: vars.color.paper, borderRadius: vars.radius.none, minWidth: 0 },
  variants: {
    pad: {
      none: { padding: 0 },
      sm: { padding: vars.space[3] },
      md: { padding: vars.space[5] },
      lg: { padding: vars.space[6] },
    },
    bordered: { true: { boxShadow: vars.shadow.edge } },
    recessed: { true: { background: vars.color.paper2, boxShadow: vars.shadow.recess } },
  },
  defaultVariants: { pad: 'md' },
})

export const measure = styleVariants({
  narrow: { maxWidth: vars.measure.narrow },
  prose: { maxWidth: vars.measure.prose },
  wide: { maxWidth: vars.measure.wide },
})

// Page — the outer editorial envelope: centred, capped width, gutters.
export const page = style({
  maxWidth: vars.layout.pageMax,
  marginInline: 'auto',
  paddingInline: vars.layout.gutter,
  width: '100%',
})
